import React, { useMemo } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router';
import { ErrorDisplay, NotFound } from '../Errors';
import { projectApi } from '../../APIs/projectApi';
import { getAsync, mutateAsync, useGet } from '../../hooks/useApi';
import {
    SpinnerIcon,
    FolderIcon,
    ExportIcon,
    EditIcon,
    UploadIcon,
    Icon,
    Icons,
    FilterOpenIcon,
    FilterClosedIcon,
} from '../atomic/icons';
import { Table } from '../atomic/table';
import { Loading } from '../atomic/effects';
import classNames from 'classnames';
import { useClickable } from '../../hooks/useClickable';
import { useReportDownloader } from '../ReportDownloader';
import { getProjectItemLink } from '../Project';
import { createCSVBlob, CSVModel, Header } from '../../utils/csv';
import { FolderItemModel, isFolderItem } from '../../models/ItemModel';
import { FilesAPI } from '../../APIs/filesApi';
import {
    createLabelTree,
    LabelTreeItem,
    maxLabelTreeDepth,
} from '../../models/AiTemplateModel';
import { parse } from 'csv-parse/sync';
import { Button, IconButton } from '../atomic/buttons';
import { ulid } from 'ulidx';
import stringify from 'json-stringify-deterministic';
import {
    EditLabelHistoryEntry,
    EditReportHistoryEntry,
    FileMetaHistoryEntry,
    HistoryType,
    TagHistoryVariant,
} from '../../utils/applyFileHistoryEntries';
import { createTimeDelay, waitFor } from '../../utils/timeDelay';
import { DialogBox, DialogBoxFooter } from '../atomic/dialog';
import { SelectField } from '../atomic/forms';
import { CheckBox, SlideSwitch } from '../atomic/switches';
import { LegendState } from '../reel/model/LocalState';
import Draggable, { DraggableEventHandler } from 'react-draggable';
import { action } from 'mobx';
import { ProgressBar } from '../atomic/progress';

export interface ProjectHeader {
    text: string;
    index: number;
    sortOrder?: 'asc' | 'desc';
    toggleSort: () => void;
}

const SortableDiv = ({ header }) => {
    const { sortOrder, text: children } = header;
    const props = useClickable(() => header.toggleSort(), [header.toggleSort]);
    const sortName = sortOrder ? `sorted` : null;

    return (
        <div className={classNames('sortable', sortName, sortOrder)} {...props}>
            {children}
        </div>
    );
};

const preventDefault = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
};

interface ImportLabelCSVHeader {
    rootLabelId: string;
    name: string;
    length: number;
}

interface ReportData {
    isLoading: boolean;
    header: Array<string>;
    rows: Array<Array<string>>;
}

interface CsvImport {
    [key: string]: Array<{
        idx: number;
        text: string;
    }>;
}

export interface FilterModel {
    idx: number; //深さ
    root: LabelTreeItem[]; // 親ラベルデータ
    aiTemplateIds: string[]; // 選択中のラベルID
    selected?: FilterModel; // 選択された下層のラベル
    name?: string; // ラベルグループの名称
    empty: boolean;
}

async function updateLabels(projectId: string, fileId: string, entries: any) {
    const update = { id: ulid(), entries: entries };
    const res = await mutateAsync(
        projectApi.mutate.updateFileMeta(projectId, fileId),
        {
            update: stringify(update),
        }
    );
    const { data } = res.data;
    if (res.status !== 200 || !data) {
        console.debug('meta upload error');
    }

    return res.status;
}

const defaultReport = {
    isLoading: true,
    header: [],
    rows: [],
};

const _Report = ({
    projectId,
    folder,
    projectName,
    folderId,
}: {
    projectId: string;
    folder: FolderItemModel;
    projectName: string;
    folderId: string;
}) => {
    const [report, setReport] = React.useState<ReportData>(defaultReport);

    const [sortedBy, setSortedBy] = React.useState<{
        index: number;
        order: 'asc' | 'desc';
    }>();

    const project = useGet(projectApi.get.project(projectId));
    const [maxLabelIdx, setMaxLabelIdx] = React.useState(0);
    const [csvImport, setCsvImport] = React.useState<CsvImport>({});
    const [message, setMessage] = React.useState<string>('');
    const [rowDiff, setRowDiff] = React.useState<{
        [key: string]: Array<number>;
    }>({});
    const [isUpdate, setIsUpdate] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    const [isImporterDialog, setIsImporterDialog] =
        React.useState<boolean>(false);

    const [percent, setPercent] = React.useState<number>(0);

    const [isNeedReload, setIsNeedReload] = React.useState(false);
    const [filter, setFilter] = React.useState<FilterModel[]>([]);
    const location = useLocation();
    const [isOpenFilter, setIsOpenFilter] = React.useState(
        location.state?.filter ? true : false
    );

    const abort = React.useRef<boolean>(false);

    const pushError = (message: string, unShift: boolean = false) => {
        setErrorMessages((prev) => {
            const list = [...prev];
            unShift ? list.unshift(message) : list.push(message);
            return list;
        });
    };

    const limit = 100;

    const getReport = async ({
        projectId: projectId,
        skipEmpty,
        includeUrl,
        limit,
        offset,
        setReport,
    }) => {
        const _report = await getAsync(
            projectApi.get.report({
                projectId,
                skipEmpty,
                includeUrl,
                limit,
                offset,
            })
        );
        const isLoading =
            _report.pagination.total > _report.pagination.nextOffset;
        setReport((prev) => {
            // 未選択項目の半角スペースを除去
            const __reports = _report.data.data.map((line) => {
                return line.map((c) => {
                    return c == ' ' ? '' : c;
                });
            });
            return {
                ...prev,
                isLoading: isLoading,
                header: _report.data.headings,
                rows: [...prev.rows, ...__reports],
            };
        });
        if (isLoading) {
            getReport({
                projectId: projectId,
                skipEmpty,
                includeUrl,
                limit,
                offset: _report.pagination.nextOffset,
                setReport,
            });
        }
    };

    const reloadReport = async () => {
        setReport(defaultReport);
        setIsNeedReload(false);
        setIsUpdate(false);
        setRowDiff({});
        setCsvImport({});
        getReport({
            projectId: projectId,
            skipEmpty: false,
            includeUrl: true,
            limit,
            offset: 0,
            setReport,
        });
        setErrorMessages([]);
        setMessage('');
    };

    React.useEffect(() => {
        reloadReport();
    }, [projectId]);

    const columns = useMemo(
        () =>
            report.header
                .filter((title, index) => {
                    return index === 2 ? false : true;
                })
                .map((title, index) => ({
                    id: `column-${index}`,
                    header: () => {
                        const header: ProjectHeader = {
                            text: title,
                            index: index,
                            sortOrder:
                                sortedBy && sortedBy.index == index
                                    ? sortedBy?.order
                                    : undefined,
                            toggleSort: () => {
                                setSortedBy((sortedBy) => {
                                    if (!sortedBy || sortedBy.index !== index) {
                                        return {
                                            index,
                                            order: 'asc',
                                        };
                                    }
                                    if (sortedBy.order === 'asc') {
                                        return {
                                            index,
                                            order: 'desc',
                                        };
                                    }
                                    return undefined;
                                });
                            },
                        };
                        return <SortableDiv header={header} />;
                    },
                    cell: (d) => {
                        const { row } = d.row.original;
                        const { rowDiff, setCsvImport } = d.table.options;
                        if (index === 0) {
                            const fileId = row[index];
                            return (
                                <Link
                                    to={getProjectItemLink({
                                        folderId,
                                        projectId,
                                        fileId,
                                    })}
                                >
                                    {fileId}
                                </Link>
                            );
                        }
                        const fileId = row[0];
                        const diffIndex = rowDiff[fileId];
                        const textColor =
                            diffIndex && diffIndex.some((i) => i === index)
                                ? 'red'
                                : '';
                        if (index === 4) {
                            const report = row[index];
                            return (report ?? '')
                                .split(/\r?\n/g)
                                .map((text, index) => (
                                    <div
                                        style={{ color: textColor }}
                                        key={index}
                                    >
                                        {text}{' '}
                                        {text.length === 0 && textColor && (
                                            <>(記事が削除)</>
                                        )}
                                    </div>
                                ));
                        }
                        const val = row[index];
                        if (Number.isNaN(val)) {
                            return <div></div>;
                        }
                        return (
                            <div style={{ color: textColor }}>
                                {val === '-' ? '0' : val}
                                {val.length === 0 && textColor && (
                                    <>(選択が解除)</>
                                )}
                            </div>
                        );
                    },
                })),
        [report.header, sortedBy, csvImport, rowDiff]
    );

    const data = useMemo(() => {
        const _filter = filter.filter((_f) => {
            return _f.selected !== undefined;
        });
        const finder = (_selected: FilterModel, row) => {
            if (_selected === undefined) {
                return true;
            }

            if (
                _selected.root.find((_f) => {
                    return _f.name === row[_selected.idx];
                })
            ) {
                return finder(_selected.selected, row);
            }
            return false;
        };

        const rowFilter = (row) => {
            if (_filter.length == 0) {
                return true;
            }
            return _filter.every((_f) => {
                if (_f.empty && row[_f.idx + 1] === '') {
                    return true;
                }
                return finder(_f.selected, row);
            });
        };
        return report.rows
            .filter((row) => rowFilter(row))
            .map((row) => {
                const getVal = (row) => {
                    return row
                        .filter((value, index) => {
                            return index === 2 ? false : true;
                        })
                        .map((value, index) => {
                            const header = report.header[index];
                            if (
                                index === 2 ||
                                index === 3 ||
                                header.endsWith('の数') ||
                                header.endsWith('の面積')
                            ) {
                                if (value === '' || value === '-') return 0;
                                return parseFloat(value);
                            }
                            return value;
                        });
                };
                return {
                    trClass: 'file',
                    row: getVal(row),
                };
            })
            .sort((a, b) => {
                if (!sortedBy) {
                    return 0;
                }
                const dataType = typeof a.row[sortedBy.index];
                if (sortedBy) {
                    if (sortedBy.order == 'asc') {
                        return a.row[sortedBy.index] > b.row[sortedBy.index]
                            ? 1
                            : -1;
                    }
                    return b.row[sortedBy.index] > a.row[sortedBy.index]
                        ? 1
                        : -1;
                }

                return 0;
            });
    }, [report.rows, sortedBy, filter]);

    const rowIds = React.useMemo(() => {
        return data.map((row) => {
            return row.row[0];
        });
    }, [data]);

    const activeFiles = useMemo(
        () =>
            report.rows.map((row) => ({
                fileId: row[0],
                fileName: row[1],
                fileUrl: row[2],
            })),
        [report.rows]
    );
    const downloader = useReportDownloader();

    const updateReportRow = (row, csv) => {
        csv.map(({ idx, text }) => {
            row[idx] = text;
        });

        return row;
    };

    // 読み込み済みレコードをインポートしたデータで上書き
    React.useEffect(() => {
        let flg = Object.entries(csvImport).length;
        const _report = { ...report };
        setIsLoading(true);
        if (project.data?.data?.template?.labels) {
            const _labelTree = createLabelTree(
                project.data?.data?.template?.labels ?? []
            );
            if (_labelTree.roots) {
                const max = _labelTree.roots
                    .map((root) => maxLabelTreeDepth(root) + 1)
                    .reduce((a, b) => {
                        return a + b;
                    }, 0);
                // ラベル項目の最大数
                setMaxLabelIdx(max);
            }
        }

        const { roots } = createLabelTree(
            project.data?.data?.template?.labels ?? []
        );
        for (const [fileId, csv] of Object.entries(csvImport)) {
            const rowIdx = _report.rows.findIndex((row, index) => {
                return row[0] === fileId;
            });

            _report.rows[rowIdx] = updateReportRow(_report.rows[rowIdx], csv);
        }

        if (flg) {
            _report.rows = [..._report.rows];
            setReport(_report);
        }
        setIsLoading(false);
    }, [project.data?.data?.template?.labels, csvImport]);

    const csvBlob = useMemo<Blob>(() => {
        const reportCsv: CSVModel<string> = {
            header: !report.isLoading
                ? report.header.map((text, id) => {
                      return { text, id: id.toString() } as Header;
                  })
                : [],
            list: !report.isLoading
                ? report.rows.map((line) => {
                      return line.map((val) => {
                          return val;
                      });
                  })
                : [],
        };
        return createCSVBlob(reportCsv);
    }, [report.rows]);

    // インポートしたCSVから変更がある行を抽出
    const updateRow = (
        params: ImportLabelCSVHeader[],
        rows: Array<Array<string>>
    ) => {
        setIsLoading(true);
        // 変更がある行とエラーを検索
        let _rowDiff: { [key: string]: Array<number> } = { ...rowDiff };
        const _labelTree = createLabelTree(
            project.data?.data?.template?.labels ?? []
        );
        const reportStartIdxForParam = (param) => {
            // 元データの開始位置
            if (param.name === '記事') return { start: 5, root: undefined };
            let _depth = 6;
            for (const root of _labelTree.roots) {
                if (root.name === param.name) {
                    return { start: ++_depth, root: root };
                }
                _depth += maxLabelTreeDepth(root) + 1;
            }

            return { start: undefined, root: undefined };
        };
        let errorCount = 0;
        const _csvImport: CsvImport = { ...csvImport };
        rows.filter((csvRow) => {
            const reportRow = report.rows.find((_report) => {
                return _report[0] === csvRow[0];
            });
            if (!reportRow) {
                pushError(`ファイルID[${csvRow[0]}]が存在しません`);
                errorCount++;
                return false;
            }

            const fileId = csvRow[0];
            let diffColumn: Array<number> = [];
            let rowStartIdx = 1;
            let _report: {
                idx: number;
                text: string;
            }[] = [];
            let error = false;
            const diff = params.filter((param) => {
                let _diff = false;
                const depth = param.length;

                const { start: reportStartIdx, root: _labelRootStart } =
                    reportStartIdxForParam(param);
                if (!reportStartIdx) {
                    pushError('・フォーマットエラー'); //ヘッダーチェック済みなので本来は来ないはず
                    error = true;
                    return false;
                }

                let _labelRoot = _labelRootStart;
                for (let idx = 0; idx < depth; idx++) {
                    const reportIdx = reportStartIdx + idx;
                    const rowIdx = rowStartIdx + idx;

                    if (_labelRoot && csvRow[rowIdx]) {
                        const label = _labelRoot.children.find((label) => {
                            return label.name === csvRow[rowIdx];
                        });
                        if (!label) {
                            pushError(
                                `・ファイルID「${csvRow[0]}」:${rowIdx}列目のラベル「${csvRow[rowIdx]}」が存在しません`
                            );
                            _labelRoot = undefined;
                            _diff = false;
                            idx = depth;
                            rowStartIdx += depth;
                            error = true;
                            return false;
                        }
                        _labelRoot = label;
                    }

                    if (reportRow[reportIdx] === csvRow[rowIdx]) {
                        continue;
                    }
                    _report.push({
                        idx: reportIdx,
                        text: csvRow[rowIdx],
                    });
                    diffColumn.push(reportIdx - 1);

                    _diff = true;
                }
                rowStartIdx += depth;
                return _diff;
            });
            if (error) {
                errorCount++;
                return false;
            }
            if (diff.length) {
                _rowDiff[fileId] = diffColumn;
                _csvImport[fileId] = _report;
            }
            return diff.length > 0;
        });
        // 変更がある行
        if (errorMessages) {
            const errorNum = errorMessages.length;
            pushError('■エラー詳細', true);
            pushError(
                `変更${
                    Object.keys(_csvImport).length
                }行 / エラー${errorCount}行`,
                true
            );
            pushError('■実行結果', true);
        }
        setRowDiff(_rowDiff);
        setCsvImport(_csvImport);
        setIsLoading(false);
    };

    // インポートしたCSVヘッダのパラメータを生成
    const createImportHeaderParams = (record) => {
        const importHeader: ImportLabelCSVHeader[] = [];
        const _labelTree = createLabelTree(
            project.data?.data?.template?.labels ?? []
        );
        if (record[0] !== 'ファイルID') {
            pushError('・「ファイルID」列を設定してください');
            return false;
        }
        const pushImport = (data, idx) => {
            let location = ++idx;
            if (
                importHeader.some((h) => {
                    return h.rootLabelId === data.rootLabelId;
                })
            ) {
                pushError(`・「${data.name}」列が${location}で重複しています`);
                return false;
            }

            importHeader.push(data);

            return true;
        };

        let hasError = false;
        for (let idx = 1; idx < record.length; idx++) {
            const title = record[idx];
            if (title === '記事') {
                if (
                    pushImport(
                        {
                            rootLabelId: '記事',
                            name: '記事',
                            length: 1,
                        },
                        idx
                    ) === false
                ) {
                    hasError = true;
                }
                continue;
            }
            // 記事以外のカラムの検索
            const roots = _labelTree.roots.find((_root) => {
                return _root.name === title;
            });

            if (!roots) {
                pushError(`・「${title}」はテンプレートに存在しません`);
                hasError = true;
                continue;
            }
            const depth = maxLabelTreeDepth(roots);
            for (let i = 0; i < depth; i++, idx++) {
                if (idx > record.length || record[idx] !== title) {
                    pushError(
                        `・「${title}」列は「${depth}」つ設定してください`
                    );
                    hasError = true;
                    break;
                }
            }
            idx--;
            if (!hasError) {
                if (
                    pushImport(
                        {
                            rootLabelId: roots.aiTemplateLabelId,
                            name: title,
                            length: depth,
                        },
                        idx
                    ) === false
                ) {
                    hasError = true;
                }
            }
            if (idx > record.length) {
                break;
            }
        }
        if (hasError) {
            return false;
        }
        return importHeader;
    };

    const handleImportLinkClick = () => {
        if (report.isLoading) {
            return;
        }
        setIsLoading(true);
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'text/csv';
        input.multiple = false; //
        input.onchange = (event) => {
            setIsImporterDialog(true);
            const files = (event.target as HTMLInputElement).files;
            if (files && files.length > 0) {
                const reader = new FileReader();
                reader.onloadend = (e) => {
                    const text = e.target?.result as string | null | undefined;
                    if (text) {
                        const records = parse(text, {
                            autoParse: true,
                            encoding: 'utf-8',
                        });
                        const header = createImportHeaderParams(records[0]);
                        if (header) {
                            updateRow(header, records.slice(1));
                        } else {
                            pushError(
                                `CSVヘッダエラーにより読み込み処理が中断されました`,
                                true
                            );
                        }
                    }
                };
                reader.readAsText(files[0]);
            }
        };
        input.click();
        setIsLoading(false);
    };

    const pushEntries = (entries, entry: FileMetaHistoryEntry) => {
        entries.push(entry);
    };

    const updateImport = async () => {
        setPercent(0);
        abort.current = false;
        setIsUpdate(true);
        setIsLoading(true);
        setIsNeedReload(true);
        setIsImporterDialog(true);
        setMessage(`更新中:0/${Object.entries(csvImport).length}`);
        const _labelTree = createLabelTree(
            project.data?.data?.template?.labels ?? []
        );
        const _csvImport = csvImport;
        setCsvImport({});

        const labelMap: {
            type: any;
            minIdx: number;
            maxIdx: number;
            root: LabelTreeItem | undefined;
        }[] = [];

        labelMap.push({
            type: HistoryType.editReport,
            minIdx: 5,
            maxIdx: 5,
            root: undefined,
        });
        let current = 6;
        _labelTree.roots.forEach((_root) => {
            current++;
            const depth = maxLabelTreeDepth(_root);
            labelMap.push({
                type: HistoryType.editLabel,
                minIdx: current,
                maxIdx: current + depth - 1,
                root: _root,
            });
            current = current + depth;
        });

        let csvIndex = 0;
        let error = false;
        for (const [fileId, fields] of Object.entries(_csvImport)) {
            if (abort.current) {
                break;
            }

            const entries: FileMetaHistoryEntry[] = [];

            labelMap.forEach((label) => {
                //Rootラベルごとに取り出して
                const _labelFields = fields
                    .filter((_f) => {
                        return _f.idx >= label.minIdx && _f.idx <= label.maxIdx;
                    })
                    .sort((a, b) => a.idx - b.idx);
                if (!_labelFields.length) {
                    return;
                }
                const reportRow = report.rows.find((_row) => {
                    return _row[0] === fileId;
                });

                if (!reportRow) {
                    pushError(`・fileId(${fileId})のファイルが存在しません`);
                    return;
                }

                const _fields: { idx: number; text: string }[] = [];
                let find = true;
                for (let i = label.minIdx; i <= label.maxIdx; i++) {
                    const _f = _labelFields.find((_f) => _f.idx === i);
                    if (_f) {
                        _fields.push(_f);
                        find = false;
                    } else if (find) {
                        _fields.push({
                            idx: i,
                            text: reportRow[i],
                        });
                    }
                }
                let _root = label.root;
                const rootAiTemplateLabelId = _root?.aiTemplateLabelId;
                // 順番に処理してidが最も大きいものだけPushする
                let _entry:
                    | EditLabelHistoryEntry
                    | EditReportHistoryEntry
                    | undefined = undefined;
                for (const _field of _fields) {
                    if (label.type === HistoryType.editReport) {
                        _entry = {
                            type: label.type,
                            report: _field.text,
                        };
                    } else {
                        if (_root && rootAiTemplateLabelId) {
                            if (_field.text === '') {
                                _entry = {
                                    type: label.type,
                                    rootLabelId: rootAiTemplateLabelId,
                                    labelId: _root.aiTemplateLabelId,
                                };
                                _root = undefined;
                            } else {
                                const childLabel = _root?.children.find(
                                    (_child) => {
                                        return _child.name === _field.text;
                                    }
                                );
                                if (childLabel) {
                                    _entry = {
                                        type: label.type,
                                        rootLabelId: rootAiTemplateLabelId,
                                        labelId: childLabel.aiTemplateLabelId,
                                    };
                                }
                                _root = childLabel;
                            }
                        }
                    }
                }
                if (_entry) {
                    pushEntries(entries, _entry);
                }
            });
            let retry = 0;
            if (entries.length) {
                while (retry < 5) {
                    try {
                        const response = await updateLabels(
                            projectId,
                            fileId,
                            entries
                        );
                        if (response === 200) {
                            break;
                        }
                    } catch (e) {
                        pushError(`ネットワークエラー`);
                        error = true;
                        break;
                    }
                    pushError(`${fileId}の更新に失敗しました`);
                    waitFor(1000);

                    retry++;
                }
            }
            if (error || retry >= 5) {
                break;
            }
            setMessage(
                `更新中:${csvIndex}/${Object.entries(_csvImport).length}`
            );
            const _p = () => {
                return csvIndex === Object.entries(_csvImport).length
                    ? 1
                    : csvIndex / Object.entries(_csvImport).length;
            };
            setPercent(_p);
            csvIndex++;
        }
        abort.current = false;
        setPercent(1);
        setIsUpdate(false);
        setIsLoading(false);
        setMessage(error ? 'エラーが発生しました' : '更新が完了しました');
    };

    interface LabelOptions {
        value: string;
        text: string;
        parent: string;
    }

    const FilterSelects = ({ filter: _filter }) => {
        const createOptions = () => {
            let options: LabelOptions[][] = [];
            _filter.root.forEach((_root) => {
                let _options = [];
                if (_root.children.length) {
                    _options.push({
                        value: _root.aiTemplateLabelId,
                        text: `（未選択）`,
                        parent: _root.name,
                    });
                }
                _root.children.forEach((_child) => {
                    const parentName = () => {
                        if (_child.parent.parent) {
                            return `${_child.parent.parent.name}:${_child.parent.name}`;
                        }
                        return _child.parent.name;
                    };

                    _options.push({
                        value: _child.aiTemplateLabelId,
                        text: `${_child.name}`,
                        parent: parentName(),
                    });
                });
                options.push([..._options]);
            });

            return options;
        };

        const templateOptions = createOptions();
        const options: Array<{
            label: string;
            options: Array<Array<LabelOptions>>;
        }> = [
            {
                label: _filter.name,
                options: templateOptions,
            },
        ];

        const setOption = (aiTemplateId: string) => {
            //rootから選択を探す

            const selectForRoot = (id) => {
                let label = undefined;
                _filter.root.forEach((_root) => {
                    if (!label) {
                        label = _root.children.find(
                            (_child) => _child.aiTemplateLabelId === id
                        );
                    }
                });
                return label;
            };

            const _root = selectForRoot(aiTemplateId);

            if (!_root) {
                _filter.empty = !_filter.empty;
            }

            // 選択ラベルリストの操作
            let deleted = false;
            _filter.aiTemplateIds = _filter.aiTemplateIds.filter((_id) => {
                if (_id === aiTemplateId) {
                    deleted = true;
                    return false;
                }
                return true;
            });
            if (!deleted) {
                _filter.aiTemplateIds.push(aiTemplateId);
            }

            _filter.aiTemplateIds = _filter.aiTemplateIds.sort();

            // ラベル選択肢の生成
            const createRootLabels = () => {
                let labels: LabelTreeItem[] = [];
                _filter.aiTemplateIds.forEach((_aiTemplateId: string) => {
                    _filter.root.forEach((_root: LabelTreeItem) => {
                        const label = _root.children.find(
                            (_child) =>
                                _child.aiTemplateLabelId === _aiTemplateId
                        );
                        if (label) {
                            labels.push(label);
                        }
                    });
                });
                return labels;
            };

            const createSelectLabel = (selected) => {
                const _selected = selected;
                // 一つも選択されていない
                if (_filter.aiTemplateIds.length === 0) {
                    return undefined;
                }
                const root = createRootLabels();
                if (!_selected) {
                    return {
                        idx: _filter.idx + 1,
                        root: root,
                        aiTemplateIds: [],
                        selected: undefined,
                    };
                }

                _selected.root = root;

                return _selected;
            };

            _filter.selected = createSelectLabel(_filter.selected);

            setFilter([...filter]);
        };

        const check = (aiTemplateLabelId: string) => {
            return _filter.aiTemplateIds.some(
                (_id) => _id === aiTemplateLabelId
            );
        };

        const hasChild = options.some((_options) => {
            return _options.options.some((_o) => {
                return _o.length > 0;
            });
        });
        if (!hasChild) {
            return <></>;
        }

        return (
            <>
                <ul
                    className={'filter-select'}
                    key={`filter-select-${_filter.idx}`}
                >
                    {options.map((parent) => {
                        return parent.options.map((_options, _index) => {
                            return (
                                <div key={`filter-select-group-div-${_index}`}>
                                    <p className="filter-select-group-title">
                                        {_options.length > 0 &&
                                            _options[1].parent}
                                    </p>
                                    <ul
                                        className={'filter-select-group'}
                                        key={`filter-select-group-${_index}`}
                                    >
                                        {_options.map((option) => {
                                            return (
                                                <li key={option.value}>
                                                    <CheckBox
                                                        label={option.text}
                                                        key={option.value}
                                                        checked={check(
                                                            option.value
                                                        )}
                                                        onChange={() => {
                                                            setOption(
                                                                option.value
                                                            );
                                                        }}
                                                    />
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            );
                        });
                    })}
                </ul>
                {_filter.selected && _filter.root && (
                    <FilterSelects
                        key={`${_filter.aiTemplateId}-child`}
                        filter={_filter.selected}
                    />
                )}
            </>
        );
    };

    const resetFilter = () => {
        const tree = createLabelTree(
            project.data?.data?.template?.labels ?? []
        );
        const _filter: FilterModel[] = [];
        let idx = 5;
        tree.roots.forEach((root) => {
            idx++;
            const _f: FilterModel = {
                idx: idx,
                root: [root],
                aiTemplateIds: [],
                selected: undefined,
                name: root.name,
                empty: false,
            };
            idx += maxLabelTreeDepth(root);
            _filter.push(_f);
        });
        setFilter(_filter);
    };

    React.useEffect(() => {
        if (location.state?.filter) {
            setFilter([...location.state?.filter]);
            setIsOpenFilter(true);
            return;
        }
        resetFilter();
    }, [project.data?.data?.template?.labels, location.state?.filter]);

    React.useEffect(() => {
        if (isOpenFilter === false) {
            resetFilter();
        }
    }, [isOpenFilter]);

    const [legend, setLegend] = React.useState<LegendState>({
        point: {
            x: 125,
            y: -24,
        },
        open: true,
    });
    const onDrag = React.useCallback<DraggableEventHandler>(
        action((e, data) => {
            legend.point.x = data.x;
            legend.point.y = data.y;
            setLegend(legend);
        }),
        []
    );

    const icon = {
        ...useClickable(
            action(() => {
                setLegend({
                    point: {
                        x: legend.point.x,
                        y: legend.point.y,
                    },
                    open: !legend.open,
                });
            }),
            [legend]
        ),
        onMouseDownCapture: preventDefault,
    };

    const navigate = useNavigate();
    const LabelFilter = useMemo(() => {
        return (
            <Draggable defaultPosition={legend.point} onDrag={onDrag}>
                <div className="report--legend--wrapper">
                    <div className="report--legend">
                        {legend.open ? (
                            <>
                                <div className="report--legend--header">
                                    <div className="title">ラベルフィルタ</div>
                                    <IconButton
                                        className="no-decoration"
                                        icon="fa-solid fa-minus"
                                        style={{ color: 'white' }}
                                        {...icon}
                                    >
                                        閉じる
                                    </IconButton>
                                </div>
                                <div className="report--legend--content">
                                    {filter.map((_f) => {
                                        return (
                                            <div key={`group-${_f.idx}`}>
                                                <FilterSelects filter={_f} />
                                            </div>
                                        );
                                    })}
                                    {project.data?.data &&
                                        rowIds.length > 0 && (
                                            <Button
                                                className={'reel-link'}
                                                style={{
                                                    margin: '0.5em 1em 0 0',
                                                }}
                                                disabled={report.isLoading}
                                                onClick={() => {
                                                    if (
                                                        project.data?.data &&
                                                        rowIds.length > 0
                                                    ) {
                                                        const to =
                                                            getProjectItemLink({
                                                                folderId:
                                                                    project.data
                                                                        ?.data
                                                                        ?.folderId,
                                                                projectId:
                                                                    project.data
                                                                        ?.data
                                                                        ?.projectId,
                                                                fileId: rowIds[0],
                                                            });
                                                        const state: {
                                                            rows: Array<string>;
                                                            filter: FilterModel[];
                                                        } = {
                                                            rows: rowIds,
                                                            filter: filter,
                                                        };
                                                        navigate(to, { state });
                                                    }
                                                }}
                                            >
                                                プロジェクトを開く(
                                                {rowIds.length}件)
                                            </Button>
                                        )}
                                </div>
                            </>
                        ) : (
                            <div className="report--legend--icon">
                                <IconButton
                                    className="no-decoration"
                                    icon="fa-regular fa-rectangle-list"
                                    {...icon}
                                >
                                    ラベルフィルタ
                                </IconButton>
                            </div>
                        )}
                    </div>
                </div>
            </Draggable>
        );
    }, [
        filter,
        rowIds,
        legend.point.x,
        legend.point.y,
        legend.open,
        report.isLoading,
    ]);

    const ImporterDialog = useMemo(() => {
        const onClick = () => {
            setIsImporterDialog(false);
            setErrorMessages([]);
            setMessage('');
            if (isNeedReload) {
                reloadReport();
                abort.current = true;
            }
        };
        const disable = (): boolean => {
            if (!isNeedReload) {
                return false;
            }
            return !(percent === 1);
        };
        const cancelText = (): string => {
            return percent === 1 ? '閉じる' : 'キャンセル';
        };

        return (
            <DialogBox
                title={isNeedReload ? 'ラベル更新' : 'ラベルインポート'}
                onOK={onClick}
                onCancel={onClick}
                cancelText={cancelText()}
                disabledCancel={!isNeedReload}
                disabled={disable()}
            >
                <div>{message}</div>
                <div className={`errorMessages`}>
                    {errorMessages.length > 0 &&
                        errorMessages.map((message, index) => {
                            return (
                                <div key={`errorMessage-${index}`}>
                                    {message}
                                </div>
                            );
                        })}
                </div>
                <DialogBoxFooter>
                    {isNeedReload && <ProgressBar percent={percent} />}
                </DialogBoxFooter>
            </DialogBox>
        );
    }, [message, errorMessages, isNeedReload, percent, abort.current]);

    const options = {
        rowDiff,
        csvImport,
    };
    return (
        <div className="app-main app-report">
            <h2>{projectName} の統計情報</h2>
            {folderId != '' && (
                <ul className={'menu-list'}>
                    <li>
                        <Link to={`/app/folderlist/${folderId}`}>
                            {report.isLoading ? (
                                <SpinnerIcon />
                            ) : (
                                <FolderIcon />
                            )}{' '}
                            フォルダページへ戻る
                        </Link>
                    </li>
                    <li>
                        <Link
                            to="#"
                            onClick={() =>
                                !report.isLoading &&
                                downloader.show({
                                    folder,
                                    csvBlob: csvBlob,
                                    projectName,
                                    activeFiles,
                                })
                            }
                        >
                            {report.isLoading ? (
                                <SpinnerIcon />
                            ) : (
                                <ExportIcon />
                            )}{' '}
                            ダウンロード
                        </Link>
                    </li>
                    {!isUpdate && maxLabelIdx > 0 && (
                        <li>
                            {Object.keys(csvImport).length === 0 && (
                                <Link to="#" onClick={handleImportLinkClick}>
                                    {report.isLoading ? (
                                        <SpinnerIcon />
                                    ) : (
                                        <UploadIcon />
                                    )}{' '}
                                    インポート
                                </Link>
                            )}
                        </li>
                    )}
                    {maxLabelIdx > 0 && (
                        <li className={classNames('atomic checkbox-group')}>
                            {isOpenFilter ? (
                                <Link
                                    to="#"
                                    onClick={() => {
                                        setIsOpenFilter(!isOpenFilter);
                                    }}
                                >
                                    <FilterOpenIcon />
                                    フィルタ
                                </Link>
                            ) : (
                                <Link
                                    to="#"
                                    onClick={() => {
                                        setIsOpenFilter(!isOpenFilter);
                                    }}
                                >
                                    <FilterClosedIcon />
                                    フィルタ
                                </Link>
                            )}
                        </li>
                    )}
                    {isOpenFilter && maxLabelIdx > 0 && LabelFilter}
                    {!isUpdate && maxLabelIdx > 0 && (
                        <>
                            {Object.keys(csvImport).length > 0 && (
                                <>
                                    <li>
                                        <Button
                                            onClick={() => updateImport()}
                                            style={{ margin: '0.5em 1em 0 0' }}
                                        >
                                            変更を反映
                                        </Button>
                                    </li>
                                    <li>
                                        <Button
                                            onClick={() => reloadReport()}
                                            style={{ margin: '0.5em 1em 0 0' }}
                                        >
                                            リロード
                                        </Button>
                                    </li>
                                </>
                            )}
                        </>
                    )}
                </ul>
            )}
            <div className="table-wrapper">
                {isLoading ? (
                    <Loading></Loading>
                ) : (
                    <Table
                        columns={columns}
                        data={data}
                        options={options}
                        className="report-table"
                    />
                )}
            </div>
            {downloader.dialog}
            {isImporterDialog && ImporterDialog}
        </div>
    );
};

export const Report = () => {
    const { projectId } = useParams();
    const project = useGet(
        projectId ? projectApi.get.project(projectId) : null
    );
    const projectName: string = project?.data?.data?.name ?? '';
    const folderId: string = project?.data?.data?.folderId ?? '';
    const folder = useGet(folderId ? FilesAPI.item(folderId) : null);

    if (!projectId) {
        return <NotFound />;
    }
    if (folder?.isLoading || project?.isLoading) {
        return <Loading />;
    }
    const error = project?.error;
    if (error) {
        return <ErrorDisplay error={error} />;
    }
    if (!folder?.data?.data?.folder) {
        return (
            <ErrorDisplay
                error={new Error('フォルダ情報の取得に失敗しました')}
            />
        );
    }
    if (!isFolderItem(folder.data.data)) {
        return (
            <ErrorDisplay error="フォルダーの代わりにファイルをロードしました。" />
        );
    }
    return (
        <_Report
            projectId={projectId}
            folder={folder.data.data}
            projectName={projectName}
            folderId={folderId}
        />
    );
};
